import { useEffect } from 'react';
import { openFile, restoreLastProject, startAutosave } from '../state/project';
import { ClickReview } from './ClickReview';
import { ExportDialog } from './ExportDialog';
import { Filmstrip } from './Filmstrip';
import { FirstRun } from './FirstRun';
import { Inspector } from './Inspector';
import { PreviewDialog } from './PreviewDialog';
import { ProjectsDialog } from './ProjectsDialog';
import { Stage } from './Stage';
import { StepRail } from './StepRail';
import { Timeline } from './Timeline';
import { TopBar } from './TopBar';
import { useShortcuts } from './shortcuts';
import { useEditor } from '../state/store';

/* Steps rail | canvas | inspector, over the full-width filmstrip (or the full timeline, behind Trim & edit video) (docs/03-design-system.md, "Editor layout"). */
export function EditorShell() {
  useShortcuts();
  const timelineView = useEditor(s => s.timelineView);
  const ready = useEditor(s => s.phase === 'ready');

  useEffect(() => {
    const stop = startAutosave();
    void restoreLastProject();
    return stop;
  }, []);

  /* a recording or .waypost file dropped anywhere opens a new project */
  useEffect(() => {
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');
    const over = (e: DragEvent) => hasFiles(e) && e.preventDefault();
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      const file = e.dataTransfer?.files[0];
      if (file) void openFile(file);
    };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, []);

  /* until there is a recording, the whole window is the drop zone */
  if (!ready)
    return (
      <>
        <FirstRun />
        <ProjectsDialog />
      </>
    );

  return (
    <div className="grid h-full min-h-[640px] min-w-[960px] gap-3 px-4 py-3 [grid-template-areas:'top_top_top'_'steps_stage_insp'_'tl_tl_tl'] grid-cols-[264px_minmax(0,1fr)_304px] grid-rows-[auto_minmax(0,1fr)_auto]">
      <TopBar />
      <StepRail />
      <Stage />
      <Inspector />
      {timelineView === 'full' ? <Timeline /> : <Filmstrip />}
      <ProjectsDialog />
      <ExportDialog />
      <PreviewDialog />
      <ClickReview />
    </div>
  );
}
